import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  Grid2,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { IconX } from "@tabler/icons-react";
import { Input } from "./Input";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import Loading from "./Loading";
import { plansUpdate } from "../services/plans";
import { useEffect, useState } from "react";
import { fightList } from "../services/fights";
import { maskMoney, unMaskMoney } from "../helpers/format";
import { Empty } from "./Empty";

const PlansUpdate = ({
  item,
  open,
  onClose,
  refetch,
}: {
  item: {
    id: string | number;
    name: string;
    value: string;
    recurrence: string;
    description?: string;
    installments?: string;
    fights: { fightId: string }[];
    free: boolean;
    paymentsType: string;
  };
  open: boolean;
  onClose: () => void;
  refetch: () => void;
}) => {
  const [value, setValue] = useState("1");
  const [free, setFree] = useState(false);
  const [recurrence, setRecurrence] = useState("MONTHLY");
  const [paymentsType, setPaymentsType] = useState<string[]>([]);
  const [fightsValues, setFightsValues] = useState<string[]>([]);

  const handleChangeValue = (
    _event: React.SyntheticEvent,
    newValue: string
  ) => {
    setValue(newValue);
  };

  const {
    data: fights,
    isLoading: isLoadingFight,
    isFetching: isFetchingFight,
  } = useQuery({
    queryKey: ["getFight"],
    queryFn: async () => await fightList({}),
    initialData: [],
  });

  const { mutate, isPending } = useMutation({
    mutationFn: plansUpdate,
    onSuccess: () => {
      onClose();
      refetch();
      toast.success("Plano atualizado");
    },

    onError: (e: { response: { data: { message: string } } }) => {
      toast.error(String(e?.response?.data?.message) || "Erro!");
    },
  });

  const handlePaymentsType = (
    _event: React.MouseEvent<HTMLElement>,
    paymentsType: string[]
  ) => {
    setPaymentsType(paymentsType);
  };

  const handleFightsValues = (
    _event: React.MouseEvent<HTMLElement>,
    fightsValues: string[]
  ) => {
    setFightsValues(fightsValues);
  };

  const { values, handleSubmit, handleChange, setFieldValue } = useFormik({
    initialValues: {
      id: "",
      name: "",
      description: "",
      free: Boolean(),
      value: "",
      installments: "1",
      recurrence: "",
      paymentsType: [],
      fights: [],
    },
    onSubmit: (values) => {
      mutate({
        ...values,
        free,
        recurrence,
        paymentsType,
        fights: fightsValues,
        value: unMaskMoney(values.value),
      });
    },
  });
  useEffect(() => {
    if (item) {
      const fightsIds = item?.fights?.map((i) => i.fightId);

      setFieldValue("id", item?.id);
      setFieldValue("name", item?.name);
      setFieldValue("description", item?.description);
      setFieldValue("value", maskMoney(Number(item?.value).toFixed(2)));
      setFieldValue("installments", String(item?.installments ?? "1"));
      setFree(item?.free);
      setRecurrence(item?.recurrence);
      setPaymentsType(item?.paymentsType?.split(",") || []);
      setFightsValues(fightsIds || []);
    }
  }, [item]);

  const parcelOptionsByRecurrence: Record<string, number[]> = {
    MONTHLY: [1],
    QUARTERLY: [1, 2, 3],
    SEMIANNUAL: [1, 2, 3, 4, 5, 6],
    YEARLY: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
  };

  return (
    <Dialog maxWidth="xs" open={open} onClose={() => onClose()}>
      <Loading loading={isPending || isLoadingFight || isFetchingFight} />
      <DialogContent sx={{ backgroundColor: "secondary.main" }}>
        <Grid2 container spacing={2} padding={1}>
          <Grid2 size={{ xs: 10, md: 11 }}>
            <Typography variant="h2" fontSize={26} fontWeight={600}>
              Atualizar plano
            </Typography>
            <Typography variant="subtitle1" color="secondary.contrastText">
              Atualize seu plano aqui.
            </Typography>
          </Grid2>
          <Grid2 size={{ xs: 2, md: 1 }}>
            <IconButton onClick={() => onClose()}>
              <IconX style={styles.iconStyle} />
            </IconButton>
          </Grid2>
          <Grid2 size={12}>
            <Tabs
              sx={{
                borderBottom: 1,
                borderColor: "divider",
                marginBottom: 2,
              }}
              onChange={handleChangeValue}
              value={value}
              textColor="inherit"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="Dados" value="1" />
              {!free && <Tab label="Valor" value="2" />}
              <Tab label="Modalidades" value="3" />
            </Tabs>
          </Grid2>
          {value === "1" && (
            <>
              <Grid2 size={12}>
                <Input
                  id="name"
                  name="name"
                  label="Nome"
                  placeholder="Digite o nome do plano"
                  type="text"
                  fullWidth
                  size="small"
                  value={values.name}
                  onChange={handleChange}
                />
              </Grid2>
              <Grid2 size={12}>
                <Input
                  id="description"
                  name="description"
                  label="Descrição"
                  placeholder="Descreva o plano"
                  type="text"
                  fullWidth
                  multiline
                  rows={4}
                  size="small"
                  value={values.description}
                  onChange={handleChange}
                />
              </Grid2>
              <Grid2 size={12}>
                <Stack
                  sx={{
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Typography variant="body1" fontSize={14}>
                    Deseja que este plano seja gratuito?
                  </Typography>
                  <Checkbox checked={free} onChange={() => setFree(!free)} />
                </Stack>
              </Grid2>
            </>
          )}
          {value === "2" && (
            <>
              <Grid2 size={12}>
                <Input
                  id="value"
                  name="value"
                  label="Valor"
                  placeholder="Digite o valor do plano"
                  type="text"
                  fullWidth
                  size="small"
                  value={values.value}
                  onChange={(e) => {
                    setFieldValue("value", maskMoney(e.target.value));
                  }}
                />
              </Grid2>

              <Grid2 size={12}>
                <Stack>
                  <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
                    Recorrência
                  </Typography>
                  <Select
                    id="recurrence"
                    value={recurrence}
                    fullWidth
                    onChange={(e) => {
                      setRecurrence(e.target.value);
                      setFieldValue("installments", 1);
                    }}
                    sx={{
                      marginTop: 1,
                    }}
                  >
                    <MenuItem value={"MONTHLY"}>Mensal</MenuItem>
                    <MenuItem value={"QUARTERLY"}>Trimestral</MenuItem>
                    <MenuItem value={"SEMIANNUAL"}>Semestral</MenuItem>
                    <MenuItem value={"YEARLY"}>Anual</MenuItem>
                  </Select>
                </Stack>
              </Grid2>
              <Grid2 size={12}>
                <Stack>
                  <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
                    Parcelas
                  </Typography>
                  <Select
                    id="installments"
                    name="installments"
                    value={values.installments}
                    onChange={handleChange}
                    fullWidth
                    sx={{ marginTop: 1 }}
                    size="small"
                  >
                    {parcelOptionsByRecurrence[recurrence]?.map((num) => (
                      <MenuItem key={num} value={num}>
                        {num}x
                      </MenuItem>
                    ))}
                  </Select>
                </Stack>
              </Grid2>
              <Grid2 size={12}>
                <Stack>
                  <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
                    Pagamentos permitidos
                  </Typography>
                  <ToggleButtonGroup
                    sx={{ marginTop: 1 }}
                    id="paymentsType"
                    value={paymentsType}
                    onChange={handlePaymentsType}
                  >
                    <ToggleButton
                      value="PIX"
                      fullWidth
                      selected={paymentsType?.includes("PIX")}
                    >
                      <Typography sx={{ fontSize: 12 }}>Pix</Typography>
                    </ToggleButton>
                    <ToggleButton
                      value="CREDIT_CARD"
                      fullWidth
                      selected={paymentsType?.includes("CREDIT_CARD")}
                    >
                      <Typography sx={{ fontSize: 12 }}>
                        Cartão de crédito
                      </Typography>
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
              </Grid2>
            </>
          )}
          {value === "3" && (
            <Grid2 size={12}>
              <Stack>
                <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
                  Modalidades
                </Typography>
                <ToggleButtonGroup
                  orientation="vertical"
                  sx={{ marginTop: 1 }}
                  id="fightsValues"
                  value={fightsValues}
                  onChange={handleFightsValues}
                >
                  {fights?.map((item: { id: string; name: string }) => (
                    <ToggleButton
                      key={item?.id}
                      value={item?.id}
                      selected={fightsValues?.includes(item?.id)}
                    >
                      <Typography sx={{ fontSize: 12 }}>
                        {item?.name}
                      </Typography>
                    </ToggleButton>
                  ))}

                  <Empty data={fights} title=" " />
                </ToggleButtonGroup>
              </Stack>
            </Grid2>
          )}
          <Grid2 size={12}>
            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={() => {
                if (value === "1") {
                  if (!values.name) {
                    toast.info("Digite o nome do plano");
                    return;
                  }
                  if (!values.description) {
                    toast.info("Digite a descrição do plano");
                    return;
                  }

                  if (free) {
                    setValue("3");
                    return;
                  }

                  setValue("2");
                }
                if (value === "2") {
                  if (!free && !values.value) {
                    toast.info(
                      "Digite o valor do plano ou selecione como gratuito!"
                    );
                    return;
                  }
                  if (paymentsType.length === 0) {
                    toast.info("Escolha um método de pagamento!");
                    return;
                  }

                  setValue("3");
                }

                if (value === "3") {
                  handleSubmit();
                }
              }}
            >
              {value === "3" ? "Atualizar plano" : "Próximo"}
            </Button>
          </Grid2>
        </Grid2>
      </DialogContent>
    </Dialog>
  );
};

const styles = {
  stackStyle: {
    flexDirection: "row",
    alignItems: "center",
    gap: 1,
    padding: 1,
    borderRadius: 2,
    backgroundColor: "secondary.dark",
  },
  iconStyle: {
    color: "primary.main",
  },
};

export default PlansUpdate;
